import React, { useEffect, useState, useRef } from 'react';
import './PreviewModal.css';
import { useEditor } from '../../contexts/EditorContext';

const PreviewModal = ({ isOpen, onClose }) => {
  const { state } = useEditor();
  const [previewContent, setPreviewContent] = useState([]);
  const [printOption, setPrintOption] = useState('current');
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const previewContainerRef = useRef(null);
  const printOptionsRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
      // Ctrl/Cmd + P for print
      if ((e.ctrlKey || e.metaKey) && e.key === 'p' && isOpen) {
        e.preventDefault();
        setShowPrintOptions(true);
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
      
      // Set all pages content for preview
      const allPages = state.pages.map(page => ({
        content: page.content,
        title: page.title,
        id: page.id
      }));
      setPreviewContent(allPages);
    }

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, state.pages]);

  // Handle clicks outside print options
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (printOptionsRef.current && !printOptionsRef.current.contains(e.target)) {
        setShowPrintOptions(false);
      }
    };

    if (showPrintOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPrintOptions]);

  // Scroll to current page when preview opens
  useEffect(() => {
    if (isOpen && previewContainerRef.current && previewContent.length > 0) {
      setTimeout(() => {
        const currentPageElement = previewContainerRef.current?.querySelector(`[data-page-index="${state.currentPageIndex}"]`);
        if (currentPageElement) {
          currentPageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [isOpen, previewContent, state.currentPageIndex]);

  const handlePrint = (pagesToPrint = 'current') => {
    let pages = [];
    
    if (pagesToPrint === 'current') {
      pages = [state.pages[state.currentPageIndex]];
    } else if (pagesToPrint === 'all') {
      pages = state.pages;
    } else {
      // Specific page
      const pageIndex = parseInt(pagesToPrint);
      if (!isNaN(pageIndex) && pageIndex >= 0 && pageIndex < state.pages.length) {
        pages = [state.pages[pageIndex]];
      }
    }

    if (pages.length === 0) return;

    const printWindow = window.open('', '_blank');
    
    // FIXED: Apply document styles properly to print
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Document - ${pages.length} page${pages.length > 1 ? 's' : ''}</title>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            margin: 0;
            padding: 0;
            font-family: ${state.design.fontFamily || 'Inter, system-ui'};
            font-size: ${state.design.fontSize || 14}px;
            line-height: ${state.design.lineHeight || 1.6};
            letter-spacing: ${state.design.letterSpacing || 'normal'};
            color: ${state.design.textColor || '#2d3748'};
            background: white;
          }
          
          @page {
            margin: 15mm;
            size: A4;
          }
          
          .print-page {
            page-break-after: always;
            max-width: 210mm;
            margin: 0 auto;
            padding: ${state.design.padding || '40px'};
            background: ${state.design.backgroundColor || 'white'};
            min-height: 297mm;
            position: relative;
          }
          
          .print-page:last-child {
            page-break-after: auto;
          }
          
          /* Inherit document styles */
          .print-page,
          .print-page * {
            font-family: ${state.design.fontFamily || 'Inter, system-ui'};
          }
          
          /* Preserve specific inline styles */
          .print-page [style*="font-family"] {
            font-family: inherit;
          }
          
          .print-page [style*="color"] {
            color: inherit;
          }
          
          .print-page [style*="background"] {
            background: inherit;
          }
          
          @media print {
            body { 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .print-page {
              padding: ${state.design.padding || '40px'} !important;
              box-shadow: none !important;
              border: none !important;
            }
            
            .page-number {
              position: fixed;
              bottom: 10mm;
              right: 10mm;
            }
          }
          
          .page-number {
            position: absolute;
            bottom: 10px;
            right: 20px;
            font-size: 11px;
            color: #999;
            font-family: ${state.design.fontFamily || 'Inter, system-ui'};
          }
          
          /* Image upload areas should not print placeholder text */
          .image-placeholder {
            display: none !important;
          }
          
          .image-upload-area {
            border: none !important;
          }
        </style>
      </head>
      <body>
        ${pages.map((page, index) => `
          <div class="print-page">
            ${page.content}
            <div class="page-number">Page ${pagesToPrint === 'current' ? state.currentPageIndex + 1 : pagesToPrint === 'all' ? index + 1 : parseInt(pagesToPrint) + 1}</div>
          </div>
        `).join('')}
        <script>
          window.onload = function() {
            // Small delay to ensure all styles are loaded
            setTimeout(() => {
              window.print();
            }, 250);
          }
          
          window.onafterprint = function() {
            setTimeout(() => window.close(), 500);
          }
          
          // Fallback: close window after 30 seconds if print dialog is cancelled
          setTimeout(() => {
            window.close();
          }, 30000);
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    setShowPrintOptions(false);
  };

  const openPrintOptions = () => {
    setShowPrintOptions(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="preview-modal-overlay">
        <div className="preview-modal">
          <div className="preview-header">
            <div className="preview-header-left">
              <h3>Preview Mode</h3>
              <span className="preview-info">
                {state.pages.length} page{state.pages.length > 1 ? 's' : ''}
                {state.pages.length > 1 && ` • Scroll to view all`}
              </span>
            </div>
            <div className="preview-header-right">
              <div className="print-options-container" ref={printOptionsRef}>
                <button 
                  className="preview-action-btn" 
                  onClick={openPrintOptions}
                  title="Print (Ctrl+P)"
                >
                  <i className="fas fa-print"></i>
                  <span>Print</span>
                </button>
                
                {showPrintOptions && (
                  <div className="print-options-menu">
                    <div className="print-section">
                      <h4>Print Options</h4>
                      <div className="print-options">
                        <label className="print-option">
                          <input
                            type="radio"
                            name="printOption"
                            value="current"
                            checked={printOption === 'current'}
                            onChange={(e) => setPrintOption(e.target.value)}
                          />
                          <span>Current Page Only</span>
                          <span className="page-indicator">Page {state.currentPageIndex + 1}</span>
                        </label>
                        
                        {state.pages.length > 1 && (
                          <>
                            <label className="print-option">
                              <input
                                type="radio"
                                name="printOption"
                                value="all"
                                checked={printOption === 'all'}
                                onChange={(e) => setPrintOption(e.target.value)}
                              />
                              <span>All Pages</span>
                              <span className="page-count-badge">{state.pages.length}</span>
                            </label>
                            
                            <div className="specific-pages-section">
                              <h5>Specific Pages:</h5>
                              <div className="specific-pages-list">
                                {state.pages.map((page, index) => (
                                  <label key={page.id} className="print-option specific">
                                    <input
                                      type="radio"
                                      name="printOption"
                                      value={index}
                                      checked={printOption === index.toString()}
                                      onChange={(e) => setPrintOption(e.target.value)}
                                    />
                                    <span>Page {index + 1}</span>
                                    <span className="page-title">{page.title}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="print-actions">
                      <button 
                        className="print-cancel-btn"
                        onClick={() => setShowPrintOptions(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="print-confirm-btn"
                        onClick={() => handlePrint(printOption)}
                      >
                        <i className="fas fa-print"></i>
                        Print Selected
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button 
                className="preview-action-btn close" 
                onClick={onClose}
                title="Close Preview (ESC)"
              >
                <i className="fas fa-times"></i>
                <span>Exit Preview</span>
              </button>
            </div>
          </div>
          
          <div className="preview-content" ref={previewContainerRef}>
            <div className="preview-documents-container">
              {previewContent.map((page, index) => (
                <div 
                  key={page.id}
                  className="preview-document"
                  data-page-index={index}
                  style={{
                    marginBottom: index < previewContent.length - 1 ? '40px' : '0'
                  }}
                >
                  <div className="page-header">
                    <span className="page-number">Page {index + 1}</span>
                    <span className="page-title">{page.title}</span>
                  </div>
                  <div 
                    className="page-content"
                    style={{
                      fontFamily: state.design.fontFamily || 'Inter, system-ui',
                      fontSize: `${state.design.fontSize || 14}px`,
                      lineHeight: state.design.lineHeight || 1.6,
                      letterSpacing: state.design.letterSpacing || 'normal',
                      color: state.design.textColor || '#2d3748',
                      backgroundColor: state.design.backgroundColor || '#ffffff',
                      padding: state.design.padding || '40px'
                    }}
                    dangerouslySetInnerHTML={{ __html: page.content }}
                  />
                </div>
              ))}
              
              {previewContent.length === 0 && (
                <div className="no-pages-message">
                  <i className="fas fa-file-alt"></i>
                  <p>No pages to preview</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="preview-footer">
            <div className="preview-stats">
              <span className="pages-count">
                <i className="fas fa-copy"></i>
                {state.pages.length} page{state.pages.length !== 1 ? 's' : ''}
              </span>
              <span className="current-page">
                <i className="fas fa-file"></i>
                Current: Page {state.currentPageIndex + 1}
              </span>
            </div>
            <div className="preview-hint">
              <i className="fas fa-info-circle"></i>
              {state.pages.length > 1 
                ? 'Scroll to view all pages • Press ESC or click Exit Preview to return'
                : 'Press ESC or click Exit Preview to return'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewModal;